'use client';

import Button from '@components/SectionComponents/Buttons/Button';
import { eventData } from '@lib/types';
import Image from 'next/image';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { getDateStringFromDates } from './_eventFunctions';
import styles from './EventTile.module.scss';
import GTMEvent from '@lib/gtm_events/GTMEvent';

// Blurb Max 240 Characters / 35 words.
// Title max 30 Characters and 4 words

const EventTile = ({
    title,
    image,
    blurb,
    button1url,
    time,
    startDate,
    endDate = startDate,
    animationClassName,
    animationDelay,
}: eventData & { animationClassName?: string; animationDelay?: string }) => {
    const [toggledState, setToggledState] = useState<boolean>(false);

    const dateString: string = useMemo(
        () => getDateStringFromDates(startDate, endDate),
        [startDate, endDate],
    );

    // Setup event button
    const decideButtonLink: ReactElement = useMemo(() => {
        let button1Text: string = 'VIEW/BOOK';
        let buttonClickFunctions = () => {
            GTMEvent('warrior_event_view', { warrior_event_name: title });
        };

        if (button1url == '' || button1url == null) {
            button1Text = 'REGISTER';
            button1url = '/events/warrior';
            buttonClickFunctions = () => {
                GTMEvent('warrior_event_Register_Click', {
                    warrior_event_name: title,
                });
                window.dispatchEvent(
                    new CustomEvent('event-select', { detail: title }),
                );
            };
        }

        {
            /**
        
        Form (listener)
        <select data-select-event  >
         <option value="Event A">Event A</option> ... 
        </select>

Mark the <select> so it’s easy to target.
In the form component (client), subscribe once and update the select:

'use client';
import { useEffect } from 'react';

export default function EventForm() {
  useEffect(() => {
    const onSelect = (e: Event) => {
      const title = (e as CustomEvent<string>).detail;
      const el = document.querySelector('[data-select-event]') as HTMLSelectElement | null;
      if (!el) return;

      // Set value and notify any listeners
      el.value = title;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    };

    window.addEventListener('event-select', onSelect);
    return () => window.removeEventListener('event-select', onSelect);
  }, []);

  return (
    <form>
      <select data-select-event /* value/defaultValue as you currently use >
        {/* options 
      </select>
      {/* rest of form 
    </form>
  );
}

Notes

Bubbles: using { bubbles: true } ensures any onChange (React or native) handlers listening up the tree will run.
Controlled vs uncontrolled: this works either way. For a controlled select, ensure your onChange updates its state from event.target.value. For an uncontrolled select, the DOM update is enough.
Timing: if the form may mount after the click, the listener will handle future events; for clicks before mount, consider also reading a URL param on mount (optional).
        
            */
        }

        return (
            <Button
                extraClasses={['eventButton', 'whiteFocus']}
                text={button1Text}
                href={button1url}
                onClick={buttonClickFunctions}
                aria-label={`${button1Text} for ${title}`}
            />
        );
    }, []);

    const hiddenContent = useRef<HTMLDivElement>(null);

    const showHide = () => {
        setToggledState(!toggledState);
    };

    return (
        <div
            className={`${styles.tileContainer} ${animationClassName ?? ''}`}
            style={animationDelay ? ({ '--drop-delay': animationDelay } as React.CSSProperties) : undefined}
        >
            <div className={styles.imgContainer}>
                <Image
                    loading="lazy"
                    src={image.path}
                    width={image.width}
                    height={image.height}
                    sizes="(max-width: 768px) 95vw, (max-width: 1280px) 30vw, 20vw"
                    alt={image.alt}
                    placeholder="blur"
                    blurDataURL={image.blurDataURL}
                    className={styles.image}
                />
            </div>
            <div className={styles.contentContainer}>
                <div className={styles.titleContainer}>
                    <h3 className={styles.title}>{title}</h3>
                    <p className={styles.dates}>
                        {time && time} {dateString}
                    </p>
                </div>
                <div
                    className={`${styles.blurbContainer}`}
                    ref={hiddenContent}
                    style={
                        toggledState
                            ? {
                                  height: `${hiddenContent.current!.scrollHeight}px`,
                                  opacity: '1',
                              }
                            : { height: '0px', opacity: '0' }
                    }
                >
                    <p
                        className={styles.blurb}
                        dangerouslySetInnerHTML={{ __html: blurb }}
                    />
                </div>
                <div className={styles.buttonsContainer}>
                    <button
                        className={styles.eventButton}
                        onClick={showHide}
                        aria-expanded={toggledState}
                        aria-label={
                            toggledState
                                ? 'Collapse event details'
                                : 'Expand event details'
                        }
                        type="button"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            fill="#FFF"
                            className={`${styles.eventButtonSVG} ${toggledState && styles.eventButtonActive}`}
                            viewBox="0 0 16 16"
                        >
                            <path
                                fillRule="evenodd"
                                d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
                            />
                        </svg>
                    </button>
                    {decideButtonLink}
                </div>
            </div>
        </div>
    );
};

export default EventTile;
